import React, { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { ArrowLeft, Loader2, Home, ChevronRight, Eye, Edit as EditIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import ProjectPageForm from '@/features/project-pages/components/ProjectPageForm'
import ProjectPagePreview from '@/features/project-pages/components/ProjectPagePreview'
import {
  useProjectPage,
  useUpdateProjectPage
} from '@/features/project-pages/hooks/useProjectPages'
import type { ProjectPageFormValues } from '@/lib/validation/projectPage'
import { toProjectPageFormData } from '@/features/project-pages/utils/toProjectPageFormData'

/**
 * Project Page Edit Page
 *
 * Provides interface for editing existing project pages.
 */
export default function ProjectPageEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit')

  const { data: projectPage, isLoading, isError } = useProjectPage(id!)
  const updateMutation = useUpdateProjectPage()

  const handleSubmit = async (data: ProjectPageFormValues) => {
    if (!id) return

    try {
      await updateMutation.mutateAsync({ id, updates: toProjectPageFormData(data) })
      navigate('/content/project-pages')
    } catch (error) {
      // Error toast already shown by mutation
      console.error('Failed to update project page:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading project page...</span>
      </div>
    )
  }

  if (isError || !projectPage) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content/project-pages')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Project Pages
        </Button>

        <Alert variant="destructive">
          <AlertDescription>
            Failed to load project page. It may have been deleted or you don't have permission to view it.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'default'
      case 'draft': return 'secondary'
      case 'archived': return 'outline'
      default: return 'secondary'
    }
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground transition-colors">
          <Home className="h-4 w-4" />
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/content/project-pages" className="hover:text-foreground transition-colors">
          Project Pages
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium">Edit</span>
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Edit Project Page</h1>
            <Badge variant={getStatusColor(projectPage.status)}>
              {projectPage.status}
            </Badge>
            {projectPage.featured && (
              <Badge variant="default" className="bg-amber-500">Featured</Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            {projectPage.title}
          </p>
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date(projectPage.updated_at).toLocaleDateString()}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/content/project-pages')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to List
        </Button>
      </div>

      {/* Edit/Preview Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'edit' | 'preview')}>
        <TabsList>
          <TabsTrigger value="edit" className="flex items-center gap-2">
            <EditIcon className="h-4 w-4" />
            Edit
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="mt-6">
          <ProjectPageForm
            initialData={projectPage}
            onSubmit={handleSubmit}
            isSubmitting={updateMutation.isPending}
          />
        </TabsContent>

        <TabsContent value="preview" className="mt-6">
          <div className="rounded-lg border bg-white overflow-hidden">
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
              <p className="text-sm text-yellow-800 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span className="font-medium">Preview Mode</span> - This is how the page will appear on fixmybrick.ca
                {projectPage.status !== 'published' && (
                  <Badge variant="secondary" className="ml-2">
                    {projectPage.status}
                  </Badge>
                )}
              </p>
            </div>
            <ProjectPagePreview projectPage={projectPage} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
